import type { ReactNode } from 'react'

import { formatClock, formatDayClock } from '../../lib/format.ts'
import {
  evidenceObject,
  mechanismPresentation,
  severityPresentation,
  tacticLabel,
} from '../../lib/detections/semantics.ts'
import { actionLabel } from '../../lib/logs/semantics.ts'
import type { AlertCondition, AlertDetails, AlertEvidence } from '../../lib/sse/types.ts'
import { CopyValue } from '../primitives/CopyValue.tsx'
import { ResponseTimeline } from './Response.tsx'
import { MitreChip, RiskMeter, SeverityBadge } from './Severity.tsx'

/**
 * O alerta por dentro: QUAL regra, ONDE ela fica na cadeia de ataque, POR QUE
 * casou e COM QUAIS eventos. É o que transforma "disparou X" em algo que o
 * analista consegue confirmar ou descartar sem abrir o TOML da regra.
 *
 * Tudo é texto do agente, exibido como texto: `expected`/`observed` podem
 * carregar a linha de comando de um atacante.
 */
export function AlertDetailsView({ alert }: { alert: AlertDetails }) {
  const severity = severityPresentation(alert.severity)
  const mechanism = mechanismPresentation(alert.mechanism)
  const stages = new Set(alert.explanation.map((c) => c.stage).filter((s) => s !== null)).size

  return (
    <div className="flex flex-col">
      {/* Cabeçalho da regra, com a faixa da severidade à esquerda. */}
      <section className={`relative border-b border-line-subtle px-4 py-3 ${severity.tint}`}>
        <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${severity.fill}`} />
        <div className="flex flex-wrap items-center gap-2">
          <SeverityBadge severity={alert.severity} size="lg" />
          <RiskMeter score={alert.risk_score} />
          {mechanism !== null && (
            <span
              className="border border-line px-1.5 py-px text-micro uppercase tracking-label text-fg-secondary"
              title={mechanism.hint}
            >
              {mechanism.label}
              {stages > 1 && ` · ${stages} passos`}
            </span>
          )}
        </div>
        {alert.rule_name !== null && (
          <p className="mt-2 text-base font-medium text-fg">{alert.rule_name}</p>
        )}
        {alert.rule_description !== null && (
          <p className="mt-1 max-w-[65ch] text-sm leading-snug text-fg-secondary">{alert.rule_description}</p>
        )}
        <dl className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-micro text-fg-muted">
          {alert.rule_id !== null && (
            <div className="flex items-center gap-1">
              <dt className="uppercase tracking-label">regra</dt>
              <dd>
                <CopyValue value={alert.rule_id} display={alert.rule_id} label="id da regra" />
              </dd>
            </div>
          )}
          {alert.rule_version !== null && (
            <div className="flex items-center gap-1">
              <dt className="uppercase tracking-label">versão</dt>
              <dd className="tnum font-mono text-fg-secondary">{alert.rule_version}</dd>
            </div>
          )}
          {alert.ruleset !== null && (
            <div className="flex items-center gap-1">
              <dt className="uppercase tracking-label">conjunto</dt>
              <dd className="font-mono text-fg-secondary">{alert.ruleset}</dd>
            </div>
          )}
        </dl>
      </section>

      {/* Logo abaixo da regra: "a máquina segue comprometida?" vem antes do porquê. */}
      {alert.response.length > 0 && (
        <Block
          title="Resposta do agente"
          aside={`${alert.response.length} ação${alert.response.length > 1 ? 'ões' : ''}`}
        >
          <ResponseTimeline response={alert.response} />
        </Block>
      )}

      {alert.threats.length > 0 && (
        <Block title="MITRE ATT&CK">
          <ol className="flex flex-col gap-1.5">
            {alert.threats.map((threat, i) => (
              <li key={i} className="flex flex-wrap items-center gap-1.5 text-sm">
                {threat.tactic !== null ? (
                  <span className="inline-flex items-center gap-1.5">
                    <MitreChip reference={threat.tactic} quiet />
                    <span className="text-fg">{tacticLabel(threat.tactic)}</span>
                  </span>
                ) : (
                  <span className="text-fg-muted">tática não informada</span>
                )}
                {threat.technique !== null && (
                  <>
                    <span aria-hidden className="text-fg-muted">
                      →
                    </span>
                    <MitreChip reference={threat.technique} />
                  </>
                )}
              </li>
            ))}
          </ol>
        </Block>
      )}

      {alert.explanation.length > 0 && (
        <Block title="Por que disparou" aside={`${alert.explanation.length} condições satisfeitas`}>
          <ExplanationList explanation={alert.explanation} />
        </Block>
      )}

      {alert.evidence.length > 0 && (
        <Block title="Evidências" aside={`${alert.evidence.length} evento${alert.evidence.length > 1 ? 's' : ''}`}>
          <ol className="relative flex flex-col">
            {alert.evidence.map((evidence, i) => (
              <EvidenceItem
                key={i}
                evidence={evidence}
                last={i === alert.evidence.length - 1}
                tone={severity.fill}
              />
            ))}
          </ol>
        </Block>
      )}

      {(alert.references.length > 0 || alert.key !== null) && (
        <Block title="Referências">
          <ul className="flex flex-col gap-0.5 font-mono text-micro text-fg-secondary">
            {alert.key !== null && (
              <li>
                <span className="text-fg-muted">chave de agrupamento · </span>
                {alert.key}
              </li>
            )}
            {alert.references.map((reference) => (
              <li key={reference} className="break-all">
                {reference}
              </li>
            ))}
          </ul>
        </Block>
      )}
    </div>
  )
}

/** As condições da regra que o evento satisfez: campo, operador, esperado e o que foi visto. */
export function ExplanationList({ explanation }: { explanation: readonly AlertCondition[] }) {
  return (
    <ol className="flex flex-col gap-1">
      {explanation.map((condition, i) => (
        <li
          key={i}
          className="grid grid-cols-[1.25rem_1fr] gap-x-2 border border-line-subtle bg-surface px-2 py-1.5"
        >
          <span className="tnum pt-px font-mono text-micro text-fg-muted">{i + 1}</span>
          <span className="min-w-0">
            <span className="flex flex-wrap items-baseline gap-x-1.5">
              {condition.stage !== null && (
                <span className="text-micro uppercase tracking-label text-fg-muted">
                  {`passo ${condition.stage + 1}`}
                </span>
              )}
              {condition.negated && (
                <span className="border border-warn px-1 text-micro uppercase tracking-label text-warn">
                  não
                </span>
              )}
              <span className="font-mono text-sm text-fg">{condition.field ?? '?'}</span>
              <span className="font-mono text-sm text-accent">{condition.operator ?? '?'}</span>
              <span className="min-w-0 font-mono text-micro break-all text-fg-secondary">
                {condition.expected ?? '—'}
              </span>
            </span>
            <span className="mt-0.5 flex items-baseline gap-1.5">
              <span className="shrink-0 text-micro uppercase tracking-label text-fg-muted">visto</span>
              <span className="min-w-0 border-l-2 border-accent bg-inset px-1.5 font-mono text-micro break-all text-fg">
                {condition.observed ?? '—'}
              </span>
            </span>
          </span>
        </li>
      ))}
    </ol>
  )
}

function EvidenceItem({ evidence, last, tone }: { evidence: AlertEvidence; last: boolean; tone: string }) {
  const object = evidenceObject(evidence)
  return (
    <li className="grid grid-cols-[1.25rem_1fr] gap-x-2">
      <span className="relative flex justify-center">
        {!last && <span aria-hidden className="absolute top-3 bottom-0 w-px bg-line" />}
        <span aria-hidden className={`relative mt-1.5 size-2 ${tone}`} />
      </span>
      <span className="min-w-0 pb-3">
        <span className="flex flex-wrap items-baseline gap-x-2">
          {evidence.timestamp !== null && (
            <span className="tnum font-mono text-micro text-fg-secondary" title={`${formatDayClock(evidence.timestamp)} UTC`}>
              {formatClock(evidence.timestamp)}
            </span>
          )}
          {evidence.stage !== null && (
            <span className="text-micro uppercase tracking-label text-fg-muted">{`passo ${evidence.stage + 1}`}</span>
          )}
          <span className="text-sm text-fg">
            {evidence.action !== null ? actionLabel(evidence.action, evidence.action) : 'evento'}
          </span>
          {evidence.code !== null && (
            <span className="font-mono text-micro text-fg-muted">#{evidence.code}</span>
          )}
        </span>
        <span className="flex flex-wrap items-baseline gap-x-2 text-micro text-fg-secondary">
          {evidence.process_name !== null && (
            <span className="font-mono text-fg">
              {evidence.parent_name !== null && (
                <span className="text-fg-muted">{evidence.parent_name} → </span>
              )}
              {evidence.process_name}
            </span>
          )}
          {evidence.pid !== null && <span className="tnum font-mono">PID {evidence.pid}</span>}
          {evidence.user !== null && <span className="font-mono">{evidence.user}</span>}
        </span>
        {object !== null && (
          <span className="mt-0.5 block border-l-2 border-line-strong bg-inset px-1.5 py-0.5 font-mono text-micro break-all text-fg-secondary">
            {object}
          </span>
        )}
      </span>
    </li>
  )
}

function Block({ title, aside, children }: { title: string; aside?: string; children: ReactNode }) {
  return (
    <section className="border-b border-line-subtle px-4 py-3">
      <h3 className="mb-2 flex items-baseline gap-2 text-micro font-medium uppercase tracking-label text-fg-secondary">
        {title}
        {aside !== undefined && <span className="font-normal normal-case tracking-[0] text-fg-muted">{aside}</span>}
      </h3>
      {children}
    </section>
  )
}
